import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, inject, Input, Output, QueryList, ViewChild, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { MentionModule } from '@syncfusion/ej2-angular-dropdowns';
import { RichTextEditorComponent, RichTextEditorModule } from '@syncfusion/ej2-angular-richtexteditor';
import { SharedService } from '../../shared.service';
import { ApiResponse, Comment, MentionUser } from '../../shared.model';
import { catchError, forkJoin, of } from 'rxjs';
import { DateAgoPipe } from '../../pipes/date-ago.pipe';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { LoaderComponent } from 'projects/sales/src/app/shared/components/loader/loader.component';

const token: string = localStorage.getItem('access_token');

@Component({
    selector: 'app-shared-comments',
    imports: [CommonModule, RichTextEditorModule, MentionModule, DateAgoPipe, ClipboardModule, LoaderComponent],
    templateUrl: './comments.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './comments.component.scss'
})
export class CommentsComponent {


  addComment: boolean = true;
  loading: boolean = false;
  tools: object = {
    enableFloating: false,
    items: [
      'Bold', 'Italic', 'Underline', 'FontColor', 'BackgroundColor', '|',
      'Alignments', 'OrderedList', 'UnorderedList', '|', 'Image',
      'CreateLink', 'ClearFormat', 'FullScreen', '|', 'Undo', 'Redo'
    ]
  }
  placeholderText = "@ to tag someone";
  comments: Comment[] = [];
  mentionUsers: MentionUser[] = [];
  addCommentRTEFocused: boolean = false;
  editCommentRTEFocused: { [key: string]: boolean } = {};
  isPrivate: boolean = false;
  isIscModule: boolean = false;
  showIsPrivateCheckBox: boolean = false;
  insertImageSettings: Object = {};
  @ViewChildren(RichTextEditorComponent) rtes: QueryList<RichTextEditorComponent>;

  @Input() enableAddComment: boolean = true;
  @Input() moduleId = null;
  @Input() module = null;
  @Input() serialNumber? = null;
  @Input() soldToBP? = null;
  @Input() showComments: boolean = false

  @ViewChild('addCommentRTE') addCommentRTE: any;

  @Output() onClose = new EventEmitter();
  private sharedService = inject(SharedService);
  private sanitizer = inject(DomSanitizer);
  private clipboard = inject(Clipboard);
  constructor(private eRef: ElementRef) { }

  @HostListener('document:click', ['$event'])
  onClick(event) {
    if (event.target.classList.value !== 'comments-opener'
      && !event.target.classList.value.includes('mentionName')
      && !event.target.classList.value.includes('mentionNameList')
      && !event.target.classList.value.includes('e-list-item')
      && !event.target.classList.value.includes('e-item')
      && !event.target.classList.value.includes('comments')
      && !event.target.classList.value.includes('e-btn-icon')
      && !event.target.classList.value.includes('e-rte-square-palette')
      && !this.eRef.nativeElement.contains(event.target)
      && !event.target.classList.value.includes('comment-action-item')
      && !this.isRteDialogClick(event.target)
    ) {
      if (document.querySelector('.swal2-container') || (event.target && event.target.parentNode && ['e-footer-content', 'e-item', 'e-dlg-header-content'].includes(event.target.parentNode.classList.value)
        || event.target.classList.contains('placeholder') || event.target.tagName === "BODY"
        || ['Align Justify', 'Align Right', 'Align Center', 'Align Left'].includes(event.target.title))) {
        // do nothing
      } else {
        this.close();
      }
    }
  }

  // On touch devices (iPad) Syncfusion renders RTE dialogs (Image, Link, etc.) as modals appended to
  // document.body, so clicks inside them or on their overlay must not be treated as outside clicks.
  private isRteDialogClick(target: HTMLElement): boolean {
    if (!target?.closest) return false;
    if (target.closest('.e-rte-elements')) return true;
    const dialogContainer = target.closest('.e-dlg-container');
    return !!dialogContainer?.querySelector(':scope > .e-rte-elements');
  }

  ngOnInit() {
    this.moduleId = this.sharedService.globalComments.moduleId;
    this.module = this.sharedService.globalComments.module;
    this.serialNumber = this.sharedService.globalComments.serialNumber;
    this.soldToBP = this.sharedService.globalComments.soldToBP;

    this.loading = true;
    let apis = [
      this.sharedService.getComments(this.moduleId, this.module),
    ]
    if(this.module === 'equipment' || this.module === 'lead') {
        apis.push(this.sharedService.getUserListForEquipmentComment())
    } else {
        apis.push(this.sharedService.getUserListForComment())
    }
    apis.push(this.sharedService.getEveryoneUsers(this.moduleId, this.module));
    apis = apis.map(api => api.pipe(catchError(error => of(error))));

    forkJoin(apis).subscribe({
      next: ([res1, res2, res3]: any) => {
        const commentsResult = res1?.result || res1?.value || [];
        if (Array.isArray(commentsResult)) {
          this.comments = commentsResult.map(c => {
            (c as any).displayName = c.createdByName || (c as any).name || 'Unknown User';
            return c;
          });
        }

        if(res3?.result && res3?.result?.length > 0) {
          let data = res3?.result as any[];
          let listOfTaggedUsers = [];
          let listOfTaggedEmails = [];

          data.forEach((d: any) => {
            listOfTaggedUsers.push(d?.name);
            listOfTaggedEmails.push(d?.email);
          });
          this.mentionUsers = [{
            "employeeId": "",
            "userId": "",
            "email": listOfTaggedEmails?.join(","),
            "listOfTaggedUsers": listOfTaggedUsers?.join(","),
            "name": "Everyone"
        },...(res2?.result || res2?.value || []) as MentionUser[]];

        } else {
          this.mentionUsers = (res2?.result || res2?.value || []) as MentionUser[];
        }
        this.loading = false;
      },
      error: err => {
        console.error('Error loading comment data:', err);
        this.loading = false;
        this.sharedService.showNotification('error', 'Failed to load comments. Please try again.');
      }
    })
  }

  sanitizedComment(comment: string): SafeHtml {
    if(comment.includes("e-rte-anchor")){
      return comment;
    } else {
      return this.sanitizer.bypassSecurityTrustHtml(comment || '');
    }
  }

  onActionBegin(args: any) {
    const mentionPopup = document.querySelector('.e-mention.e-popup-open');
    if (args.requestType === 'EnterAction' && mentionPopup !== null) {
      args.cancel = true;
    }
  }

  onImageUploading(args) {
    args.currentRequest.setRequestHeader('Authorization', `Bearer ${token}`);
  }

  removeCommentFromList(index: number) {
    this.comments.splice(index, 1);
  }

deleteComment(commentId) {
    this.sharedService.showConfirmation(
      'Are you sure you want to delete this Comment? This cannot be undone!',
      'Yes',
      'No',
      'Are You Sure?'
    ).then(res => {
      if (res && res.isConfirmed) {
        this.loading = true;
        this.sharedService.deleteCommentByCommentId(commentId).subscribe({
          next: (response) => {
            if (response) {
              const index = this.comments.findIndex(comment => comment.commentId === commentId);
              this.removeCommentFromList(index);
              this.updateCommentCountAndColor();
            }
            this.loading = false;
          },
          error: (err) => {
            this.loading = false;
            throw err;
          },
        });
      }
    });
  }

  onActionComplete(args, commentRTE: RichTextEditorComponent) {
    ['Maximize', 'Minimize'].includes(args.requestType) && commentRTE.refreshUI();
  }

  updateComment(comment, commentRTE) {
    this.saveComment(commentRTE, false, comment.commentId, comment);
  }

  updateCommentCountAndColor() {
    this.sharedService.globalComments.commentCount = this.comments.length;
    if (this.comments.length) {
      this.sharedService.globalComments.commentColor = '#ff8403';
    } else {
      this.sharedService.globalComments.commentColor = '#555555';
    }
  }

  saveComment(commentRTE: RichTextEditorComponent, close: boolean = false, commentId?, commentObj?: any) {
    // Force direct sync if value seems empty
    let commentValue = commentRTE?.value;
    
    // Check if effective content exists
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = commentValue || '';
    const textContent = tempDiv.innerText?.trim() || '';
    const hasImages = tempDiv.querySelectorAll('img').length > 0;

    if (!textContent && !hasImages) {
      this.sharedService.showNotification('info', 'Please enter some text in the comment box.');
      return;
    }
    
    if (!this.moduleId || !this.module) {
      this.sharedService.showNotification('error', 'Application context missing. Please try reopening the comments.');
      return;
    }

    const payload = {
      commentId: commentId || 0,
      moduleName: this.module,
      primaryId: this.moduleId,
      comment: commentValue,
      isPrivate: commentObj ? commentObj.isPrivate : this.isPrivate
    };

    this.serialNumber && (payload['serialNumber'] = this.serialNumber);
    this.serialNumber && (payload['secondaryId'] = this.serialNumber);

    this.loading = true;
    this.sharedService.saveComment(payload).subscribe({
      next: (res: any) => {
        // More flexible success check: check for explicit success flags OR existence of local result
        if (res && (res.isSuccessful || res.statusCode === 200 || (res.result && !res.apiException))) {
          const commentsResult = res?.result || res?.value || [];
          if (Array.isArray(commentsResult)) {
            this.comments = commentsResult.map(c => {
              (c as any).displayName = c.createdByName || (c as any).name || 'Unknown User';
              return c;
            });
          }
          this.updateCommentCountAndColor();
          
          if (commentObj) {
            commentObj.beingEdited = false;
          } else {
            commentRTE.value = null; 
            this.addComment = false;
          }
          this.isPrivate = false;
          this.sharedService.showNotification('success', res?.message || 'Comment saved successfully!', 'top-end', 2000);
          close && this.close();
        } else {
          this.sharedService.showNotification('error', res?.message || 'Error occurred while saving comment.');
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.sharedService.showNotification('error', 'Failed to connect to the server. Please try again.');
      }
    });
  }

  async close() {
    if (await this.canClose()) {
      this.onClose.emit();
    }
  }

  async onCancel(commentRTE: RichTextEditorComponent) {
    commentRTE.value = null;
    this.addComment = false;
    this.isPrivate = false;
  }

  async canClose() {
    let hasUnsavedContent = false;
    this.rtes.forEach(rte => {
      if (rte.value && rte.value !== '<p><br></p>') {
        hasUnsavedContent = true;
      }
    });

    if (hasUnsavedContent) {
      const confirmed = await this.sharedService.showConfirmation('You have unsaved changes. Are you sure you want to close?', 'Yes', 'No', 'Unsaved Changes');
      return (confirmed && confirmed.isConfirmed);
    }
    return true;
  }

  copyComment(content: string) {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = content;
    const plainText = tempElement.innerText || tempElement.textContent || '';
    const success = this.clipboard.copy(plainText);
    if (success) {
      this.sharedService.showNotification('success', 'Comment copied to clipboard!', 'top-end', 2000);
    }
  }

  onAddCommentRTEFocus() {
    this.addCommentRTEFocused = true;
  }

  onAddCommentRTEBlur() {
    setTimeout(() => {
      this.addCommentRTEFocused = false;
    }, 80);
  }

  onEditCommentRTEFocus(commentId: string) {
    this.editCommentRTEFocused[commentId] = true;
  }

  onEditCommentRTEBlur(commentId: string) {
    setTimeout(() => {
      this.editCommentRTEFocused[commentId] = false;
    }, 80);
  }
}
